import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ListTodo, AlertTriangle, Clock, CheckCircle2, ChevronRight, Users, Zap, Loader2, ArrowRight, ChevronDown, User, CalendarPlus, CalendarCheck, XCircle, RotateCcw, ListPlus, Check, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FollowupOutcomeDialog } from '../components/FollowupOutcomeDialog';
import { useAuth } from '../contexts/AuthContext';
import { PrioBadge } from '../components/PrioCriteriaPicker';

const taskErrMsg = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (d && typeof d === 'object' && d.code === 'stage_gate') return `Missing for ${d.target_label}: ${(d.missing || []).join(' · ')}`;
  if (d && typeof d === 'object' && d.message) return d.message;
  return typeof d === 'string' ? d : fallback;
};
// Contact-enrich tasks (Talu, 22.10.2026): anyone may do them or take them over
const isEnrichTask = (t) => ['olta_approved_enrich_contact', 'outreach_step_enrich'].includes(t?.template_id);
const PRIO_ORDER = { A: 0, B: 1, C: 2 };

const urgency = (t) => {
  if (!t.due_at) return 'none';
  const days = (new Date(t.due_at) - Date.now()) / 86400000;
  if (days < 0) return 'overdue';
  if (days < 1) return 'today';
  if (days <= 7) return 'week';
  return 'later';
};

const URGENCY_META = {
  overdue: { label: 'Overdue', bar: 'bg-red-500', text: 'text-red-400' },
  today: { label: 'Due today', bar: 'bg-amber-500', text: 'text-amber-400' },
  week: { label: 'Next 7 days', bar: 'bg-blue-500', text: 'text-blue-400' },
  later: { label: 'Later', bar: 'bg-emerald-500', text: 'text-emerald-400' },
  none: { label: 'No due date', bar: 'bg-border', text: 'text-muted-foreground' },
};
const GROUP_ORDER = ['overdue', 'today', 'week', 'later', 'none'];

const fmtDue = (iso) => {
  if (!iso) return '—';
  const days = (new Date(iso) - Date.now()) / 86400000;
  const totalH = Math.round(Math.abs(days) * 24);
  const d = Math.floor(totalH / 24);
  const h = totalH % 24;
  const str = d > 0 ? `${d}d ${h}h` : `${h}h`;
  return days < 0 ? `${str} overdue` : `${str} left`;
};

const typeKey = (t) => {
  if ((t.title || '').startsWith('💬')) return '💬 Mentions';
  const i = (t.title || '').indexOf('·');
  return i > 0 ? t.title.slice(0, i).trim() : (t.title || '');
};

const isSeqCall = (t) => t?.seq_channel === 'call'
  || ((t?.template_id || '').startsWith('seq:') && (t?.title || '').startsWith('Call '));

function TypeGroup({ label, tasks, renderRow, onBulk, bulking }) {
  const [open, setOpen] = useState(false);
  const worst = URGENCY_META[urgency(tasks[0])];
  return (
    <div className="border border-border rounded-sm overflow-hidden" data-testid={`task-type-group-${label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
      <div className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-card">
        <button onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
          data-testid={`task-group-toggle-${label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className="text-sm text-foreground font-medium truncate">{label}</span>
          <span className="text-[10px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5 shrink-0">{tasks.length}×</span>
        </button>
        <span className={`text-xs font-mono shrink-0 ${worst.text}`}>{fmtDue(tasks[0].due_at)}</span>
        {onBulk && (
          <button onClick={() => onBulk(tasks)} disabled={bulking}
            data-testid={`task-group-bulk-${label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
            className="shrink-0 flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
            {bulking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Alle erledigen
          </button>
        )}
      </div>
      {open && <div className="p-2 space-y-2 bg-background/50 border-t border-border">{tasks.map(renderRow)}</div>}
    </div>
  );
}

function GroupedTaskList({ tasks, renderRow, onBulk, bulking, canBulkTask }) {
  const byType = [];
  tasks.forEach(t => {
    const k = typeKey(t);
    const e = byType.find(x => x.key === k);
    if (e) e.tasks.push(t); else byType.push({ key: k, tasks: [t] });
  });
  return byType.map(b => b.tasks.length >= 3
    ? <TypeGroup key={b.key} label={b.key} tasks={b.tasks} renderRow={renderRow} bulking={bulking}
        onBulk={onBulk && canBulkTask && b.tasks.every(canBulkTask) ? onBulk : undefined} />
    : b.tasks.map(renderRow));
}

function SummaryCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4 flex items-center gap-3" data-testid={testId}>
      <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${accent}`}>
        <Icon style={{ width: 18, height: 18 }} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-heading text-foreground leading-none">{value}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{label}</p>
      </div>
    </div>
  );
}

function TaskRow({ task, onOpenDeal, onComplete, completing, canComplete, showAssignee, myId, onPushTodo, pushing, onClaim, claiming }) {
  const u = URGENCY_META[urgency(task)];
  return (
    <div className="flex items-stretch gap-3 bg-card border border-border rounded-sm overflow-hidden" data-testid={`mytask-row-${task.id}`}>
      <div className={`w-1 shrink-0 ${u.bar}`} />
      <div className="flex-1 min-w-0 py-3 pr-2">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-foreground font-medium">{task.title}</p>
          {task.is_primary && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono uppercase flex items-center gap-1">
              <Zap className="w-2.5 h-2.5" /> Primary
            </span>
          )}
          {task.advances_to && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-blue-500/30 bg-blue-500/10 text-blue-400 font-mono flex items-center gap-1">
              <ArrowRight className="w-2.5 h-2.5" /> {task.advances_to}
            </span>
          )}
          {task.pass_proposed_by_id && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-400 font-mono uppercase flex items-center gap-1" data-testid={`mytask-pass-proposed-${task.id}`}>
              <XCircle className="w-2.5 h-2.5" />
              {task.pass_proposed_by_id === myId ? 'Pass proposed — awaiting approval' : `Pass proposed by ${task.pass_proposed_by}`}
            </span>
          )}
          {task.on_agenda && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-purple-500/30 bg-purple-500/10 text-purple-400 font-mono uppercase flex items-center gap-1" title="On the team meeting agenda" data-testid={`mytask-agenda-${task.id}`}>
              <CalendarCheck className="w-2.5 h-2.5" /> Agenda
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <button onClick={() => onOpenDeal(task.project_id)} data-testid={`mytask-deal-link-${task.id}`}
            className="text-[11px] text-blue-400 hover:text-blue-300 font-medium truncate max-w-[220px]">
            {task.code_name || task.deal_name}
          </button>
          {task.company_name && (
            <span className="text-[11px] text-muted-foreground truncate max-w-[220px]" data-testid={`mytask-company-${task.id}`}>· {task.company_name}</span>
          )}
          <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground font-mono uppercase">{task.stage_label}</span>
          {task.prio && <PrioBadge prio={task.prio} testid={`mytask-prio-${task.id}`} />}
          {showAssignee && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <User className="w-3 h-3" /> {task.assignee_name || 'Unassigned'}
            </span>
          )}
          {task.claimed_from_name && (
            <span className="text-[9px] px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300 font-mono flex items-center gap-1" title={`Übernommen von ${task.claimed_from_name}`} data-testid={`mytask-claimed-${task.id}`}>
              <UserPlus className="w-2.5 h-2.5" /> übernommen{showAssignee ? '' : ` von ${task.claimed_from_name.split(' ')[0]}`}
            </span>
          )}
          {isEnrichTask(task) && !task.claimed_from_name && (
            <span className="text-[9px] text-muted-foreground/70 font-mono" data-testid={`mytask-anyone-${task.id}`}>· jeder kann übernehmen</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 pr-3 shrink-0">
        <span className={`text-xs font-mono ${u.text}`}>{fmtDue(task.due_at)}</span>
        {onPushTodo && (
          <Button size="sm" variant="ghost" onClick={() => onPushTodo(task)} disabled={pushing === task.id}
            title="Push to Microsoft To Do" data-testid={`mytask-push-todo-${task.id}`}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-sky-400">
            {pushing === task.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListPlus className="w-4 h-4" />}
          </Button>
        )}
        {onClaim && isEnrichTask(task) && task.assignee_id !== myId && (
          <Button size="sm" variant="outline" onClick={() => onClaim(task)} disabled={claiming === task.id}
            title="Aufgabe übernehmen — der bisherige Zuständige wird informiert"
            className="h-7 px-2.5 text-[11px] rounded-sm border-border hover:border-sky-500/50 hover:text-sky-300"
            data-testid={`mytask-claim-btn-${task.id}`}>
            {claiming === task.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><UserPlus className="w-3 h-3 mr-1" /> Übernehmen</>}
          </Button>
        )}
        {(canComplete || isEnrichTask(task)) && (
          <Button size="sm" variant="outline" onClick={() => onComplete(task)} disabled={completing === task.id}
            className="h-7 px-2.5 text-[11px] rounded-sm border-border hover:border-emerald-500/50 hover:text-emerald-400"
            data-testid={`mytask-complete-btn-${task.id}`}>
            {completing === task.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : task.auto_complete_on === 'agenda_added' ? <><CalendarPlus className="w-3 h-3 mr-1" /> Add to Agenda</>
              : 'Complete'}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function MyTasksPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmTask, setConfirmTask] = useState(null);
  const [outcomeTask, setOutcomeTask] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [pushing, setPushing] = useState(null);
  const [showOthers, setShowOthers] = useState(false);
  const [bulking, setBulking] = useState(false);

  const load = useCallback(() => {
    api.getTasks()
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load tasks'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleComplete = (task) => {
    if (task.source === 'outreach_followup' || isSeqCall(task)) return setOutcomeTask(task);
    if (task.auto_complete_on === 'agenda_added') return doAgenda(task);
    if (task.is_primary && task.advances_to) setConfirmTask(task);
    else doComplete(task);
  };

  const doCompleteOutcome = async (outcome, extra) => {
    const task = outcomeTask;
    setCompleting(task.id);
    try {
      await api.completeTask(task.id, outcome ? {
        followup_outcome: outcome,
        ...(extra?.category ? { followup_reason_category: extra.category } : {}),
        ...(extra?.reason ? { followup_reason_text: extra.reason } : {}),
      } : undefined);
      toast.success(outcome ? 'Call result saved — task completed' : 'Task completed');
      setOutcomeTask(null);
      load();
    } catch (e) {
      toast.error(taskErrMsg(e, 'Could not complete task'));
    } finally {
      setCompleting(null);
    }
  };

  const doAgenda = async (task) => {
    setCompleting(task.id);
    try {
      await api.addToAgenda(task.project_id);
      toast.success('Added to the team meeting agenda — task completed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add to agenda');
    } finally {
      setCompleting(null);
    }
  };

  const [claiming, setClaiming] = useState(null);
  const doClaim = async (task) => {
    setClaiming(task.id);
    try {
      await api.claimTask(task.id);
      toast.success(`„${task.title}“ übernommen${task.assignee_name ? ` — ${task.assignee_name.split(' ')[0]} wurde informiert` : ''}`);
      load();
    } catch (e) { toast.error(taskErrMsg(e, 'Übernehmen fehlgeschlagen')); }
    setClaiming(null);
  };

  const doComplete = async (task) => {
    setCompleting(task.id);
    try {
      const r = await api.completeTask(task.id);
      if (r.data.new_stage_label) toast.success(`Task completed — deal moved to ${r.data.new_stage_label}`);
      else toast.success('Task completed');
      load();
    } catch (e) {
      toast.error(taskErrMsg(e, 'Could not complete task'));
    } finally {
      setCompleting(null);
      setConfirmTask(null);
    }
  };

  const doPushTodo = async (task) => {
    setPushing(task.id);
    try {
      await api.pushTaskToTodo(task.id);
      toast.success('In Microsoft To Do übernommen');
    } catch (e) {
      toast.error(taskErrMsg(e, 'Push zu MS To Do fehlgeschlagen'));
    } finally {
      setPushing(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!data) return null;

  const s = data.summary;
  const groups = {};
  data.mine.forEach(t => { const u = urgency(t); (groups[u] = groups[u] || []).push(t); });
  const openDeal = (id) => navigate(id ? `/projects/${id}` : '/broker-campaigns');
  const canComplete = (t) => t.assignee_rule !== 'admin' || data.is_admin;
  const canBulkTask = (t) => !t.event && !t.is_primary && canComplete(t);
  const handleBulk = async (tasks) => {
    if (!window.confirm(`${tasks.length} Tasks als erledigt markieren?`)) return;
    setBulking(true);
    try {
      const r = await api.bulkCompleteTasks(tasks.map(t => t.id));
      toast.success(`${r.data.completed} Tasks erledigt${r.data.skipped ? ` · ${r.data.skipped} übersprungen` : ''}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBulking(false);
  };
  const myId = user?.id || data.user_id;
  // "Kontakt anlegen" tasks of teammates are claimable by anyone — Max wants them right under his own tasks (26.10.2026)
  const claimable = data.others.filter(t => isEnrichTask(t) && t.assignee_id !== myId)
    .sort((a, b) => (PRIO_ORDER[a.prio] ?? 9) - (PRIO_ORDER[b.prio] ?? 9) || (a.due_at || '').localeCompare(b.due_at || ''));
  const otherTasks = data.others.filter(t => !claimable.includes(t));

  const dismissReactivated = async (id) => {
    try {
      await api.ackReactivation(id);
      setData(d => ({ ...d, reactivated: (d.reactivated || []).filter(r => r.id !== id) }));
    } catch { toast.error('Could not dismiss'); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="my-tasks-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
          <ListTodo className="w-6 h-6 text-blue-400" /> My Tasks
        </h1>
        <p className="text-muted-foreground text-sm mt-1" data-testid="tasks-headline">
          {(s.overdue + s.due_today) > 0 || (s.inactive_deals || 0) > 0
            ? <>You have{(s.overdue + s.due_today) > 0 && <> <span className="text-amber-400 font-medium">{s.overdue + s.due_today} task{s.overdue + s.due_today > 1 ? 's' : ''}</span> overdue or due today</>}{(s.overdue + s.due_today) > 0 && (s.inactive_deals || 0) > 0 && <> and</>}{(s.inactive_deals || 0) > 0 && <> <span className="text-red-400 font-medium">{s.inactive_deals} inactive deal{s.inactive_deals > 1 ? 's' : ''}</span> needing attention</>}.</>
            : s.open > 0 ? 'Nothing urgent — all tasks are on track.' : 'No open tasks right now.'}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={ListTodo} label="My open tasks" value={data.mine.length} accent="bg-blue-500/10 text-blue-400" testId="tasks-summary-open" />
        <SummaryCard icon={AlertTriangle} label="Overdue" value={s.overdue} accent="bg-red-500/10 text-red-400" testId="tasks-summary-overdue" />
        <SummaryCard icon={Clock} label="Due today" value={s.due_today} accent="bg-amber-500/10 text-amber-400" testId="tasks-summary-due-today" />
        <SummaryCard icon={Users} label="Unassigned" value={s.unassigned} accent="bg-purple-500/10 text-purple-400" testId="tasks-summary-unassigned" />
      </div>

      {data.mine.length === 0 && data.unassigned.length === 0 && claimable.length === 0 && (data.inactive_deals || []).length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-10 text-center" data-testid="tasks-empty-state">
          <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
          <p className="text-sm text-foreground">All caught up</p>
          <p className="text-xs text-muted-foreground mt-1">Tasks appear here automatically when your claimed deals enter a new pipeline stage.</p>
        </div>
      ) : (
        <>
          {GROUP_ORDER.filter(g => groups[g]?.length).map(g => (
            <div key={g} className="space-y-2">
              <p className={`text-[10px] uppercase tracking-wider font-mono ${URGENCY_META[g].text}`}>
                {URGENCY_META[g].label} · {groups[g].length}
              </p>
              <GroupedTaskList tasks={groups[g]} onBulk={handleBulk} bulking={bulking} canBulkTask={canBulkTask} renderRow={(t) => (
                <TaskRow key={t.id} task={t} onOpenDeal={openDeal} onComplete={handleComplete}
                  completing={completing} canComplete={canComplete(t)} showAssignee={false} myId={myId} onPushTodo={doPushTodo} pushing={pushing} />
              )} />
            </div>
          ))}

          {claimable.length > 0 && (
            <div className="space-y-2" data-testid="tasks-claimable-section">
              <p className="text-[10px] uppercase tracking-wider font-mono text-sky-300 flex items-center gap-1.5">
                <UserPlus className="w-3.5 h-3.5" /> Kontakt anlegen — zum Übernehmen · {claimable.length}
              </p>
              <p className="text-[11px] text-muted-foreground -mt-1">Kontakt-Aufgaben der Kollegen, sortiert nach Deal-Prio — „Übernehmen“ holt sie in deine Liste, damit die Briefe weitergehen.</p>
              {claimable.map(t => (
                <TaskRow key={t.id} task={t} onOpenDeal={openDeal} onComplete={handleComplete}
                  completing={completing} canComplete={canComplete(t)} showAssignee={true} myId={myId}
                  onClaim={doClaim} claiming={claiming} />
              ))}
            </div>
          )}

          {data.unassigned.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider font-mono text-purple-400 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Unassigned team tasks · {data.unassigned.length}
              </p>
              <GroupedTaskList tasks={data.unassigned} onBulk={handleBulk} bulking={bulking} canBulkTask={canBulkTask} renderRow={(t) => (
                <TaskRow key={t.id} task={t} onOpenDeal={openDeal} onComplete={handleComplete}
                  completing={completing} canComplete={canComplete(t)} showAssignee={false} myId={myId} onPushTodo={doPushTodo} pushing={pushing} />
              )} />
            </div>
          )}
        </>
      )}

      {(data.inactive_deals || []).length > 0 && (
        <div className="space-y-2" data-testid="inactive-deals-section">
          <p className="text-[10px] uppercase tracking-wider font-mono text-red-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Inactive deals you own · {data.inactive_deals.length}
          </p>
          {data.inactive_deals.map(d => (
            <div key={d.id} className="flex items-stretch gap-3 bg-card border border-red-500/20 rounded-sm overflow-hidden" data-testid={`inactive-deal-${d.id}`}>
              <div className="w-1 shrink-0 bg-red-500" />
              <div className="flex-1 min-w-0 py-3 pr-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => openDeal(d.id)} data-testid={`inactive-deal-link-${d.id}`}
                    className="text-sm text-foreground font-medium hover:text-red-400 transition-colors">
                    {d.code_name || d.name}
                  </button>
                  {d.company_name && <span className="text-[11px] text-muted-foreground truncate max-w-[220px]">· {d.company_name}</span>}
                  <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground font-mono uppercase">{d.stage_label}</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  No recent activity on this deal — move it forward or propose a pass
                  {d.reminded_at ? ` (flagged ${new Date(d.reminded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })})` : ''}.
                </p>
              </div>
              <div className="flex items-center pr-3 shrink-0">
                <Button size="sm" variant="outline" onClick={() => openDeal(d.id)}
                  className="h-7 px-2.5 text-[11px] rounded-sm border-border hover:border-red-500/50 hover:text-red-400"
                  data-testid={`inactive-deal-open-${d.id}`}>
                  Open deal
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(data.reactivated || []).length > 0 && (
        <div className="space-y-2" data-testid="reactivated-deals-section">
          <p className="text-[10px] uppercase tracking-wider font-mono text-emerald-400 flex items-center gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Reactivated deals · {data.reactivated.length}
          </p>
          {data.reactivated.map(d => (
            <div key={d.id} className="flex items-stretch gap-3 bg-card border border-emerald-500/20 rounded-sm overflow-hidden" data-testid={`reactivated-deal-${d.id}`}>
              <div className="w-1 shrink-0 bg-emerald-500" />
              <div className="flex-1 min-w-0 py-3 pr-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => openDeal(d.id)} data-testid={`reactivated-deal-link-${d.id}`}
                    className="text-sm text-foreground font-medium hover:text-emerald-400 transition-colors">
                    {d.code_name || d.name}
                  </button>
                  {d.company_name && <span className="text-[11px] text-muted-foreground truncate max-w-[220px]">· {d.company_name}</span>}
                  <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground font-mono uppercase">{d.stage_label}</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  You proposed a pass{d.original_reason ? ` ("${d.original_reason.slice(0, 80)}${d.original_reason.length > 80 ? '…' : ''}")` : ''} — <span className="text-emerald-400 font-medium">{d.overridden_by}</span> kept the deal active
                  {d.overridden_at ? ` on ${new Date(d.overridden_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : ''}.
                </p>
                {d.reason && <p className="text-[11px] text-foreground/80 mt-0.5" data-testid={`reactivated-reason-${d.id}`}>Reasoning: "{d.reason}"</p>}
              </div>
              <div className="flex items-center pr-3 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => dismissReactivated(d.id)}
                  className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                  data-testid={`reactivated-dismiss-${d.id}`}>
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {otherTasks.length > 0 && (
        <div>
          <button onClick={() => setShowOthers(o => !o)} data-testid="tasks-others-toggle"
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOthers ? '' : '-rotate-90'}`} />
            Other members' tasks ({otherTasks.length})
          </button>
          {showOthers && (
            <div className="mt-2 space-y-2">
              {otherTasks.map(t => (
                <TaskRow key={t.id} task={t} onOpenDeal={openDeal} onComplete={handleComplete}
                  completing={completing} canComplete={data.is_admin} showAssignee={true} myId={myId} onPushTodo={doPushTodo} pushing={pushing}
                  onClaim={doClaim} claiming={claiming} />
              ))}
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground/60">
        Tasks are generated automatically from the deal's pipeline stage. Completing a <span className="text-amber-400">Primary</span> task with an arrow moves the deal to the next stage.
        Due dates come from the stage SLA (business days). Unassigned tasks belong to unclaimed deals — anyone on the team can complete them.
      </p>

      <FollowupOutcomeDialog task={outcomeTask} busy={!!completing}
        onSelect={doCompleteOutcome} onSkip={() => doCompleteOutcome(null)} onClose={() => setOutcomeTask(null)} />

      <Dialog open={!!confirmTask} onOpenChange={(o) => !o && setConfirmTask(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="mytask-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" /> Complete & advance?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Completing <span className="text-foreground font-medium">"{confirmTask?.title}"</span> will move{' '}
            <span className="text-foreground font-medium">{confirmTask?.code_name || confirmTask?.deal_name}</span> to{' '}
            <span className="text-blue-400 font-medium">{confirmTask?.advances_to}</span>.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setConfirmTask(null)} className="text-xs h-8 px-3">Cancel</Button>
            <Button onClick={() => doComplete(confirmTask)} disabled={!!completing} data-testid="mytask-confirm-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-xs h-8 px-3">
              {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Complete & advance'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
